'use client';

import { ReactNode, memo } from 'react';

import { StrongTeam } from '@nf/types/odds';
import { TeamCard } from '../team-card';
import { TeamDot } from '../team-dot';
import { matchEquals } from '@nf/utils-common/fpw/monadic';
import { stringEmptyCheck } from '@nf/utils-common/fpw/utils';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';

interface MatchTeamsProps {
	teamIdA: number;
	teamIdB: number;
	teamNameA: string;
	teamNameB: string;
	scoreA: string;
	scoreB: string;
	isLive: boolean;
	homeRedCardCount: number;
	awayRedCardCount: number;
	sportId: number;
	serveTeam: string | undefined;
	strongTeam: StrongTeam;
};
export const MatchTeams = memo(function MatchTeams({
	teamIdA,
	teamIdB,
	teamNameA,
	teamNameB,
	scoreA,
	scoreB,
	isLive,
	homeRedCardCount,
	awayRedCardCount,
	sportId,
	serveTeam,
	strongTeam
}: MatchTeamsProps) {
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();

	const dotTemplate = (team: 'teamA' | 'teamB'): ReactNode => {
		return matchEquals(sportId)
			.on(44, () => {
				return <TeamDot variant={team === 'teamA' ? 'red' : 'blue'} />
			})
			.otherwise(() => {
				if (!isLive) return;
				return stringEmptyCheck(serveTeam)
					.fold(
						() => { },
						() => {
							const controlSports = [5, 6, 9, 18];
							const showServe = !controlSports.includes(sportId) || siteConfig?.EnableDisplayServe;
							const isActive: boolean = matchEquals(team)
								.on('teamA', () => serveTeam === '1')
								.on('teamB', () => serveTeam === '2')
								.otherwise(() => false);

							return showServe ? (
								<TeamDot 
									variant="serve" 
									isActive={isActive} 
								/>
							) : null;
						}
					)
			});
	}

	return (
		<div className="match-teams">
			<TeamCard
				dotTemplate={() => dotTemplate('teamA')}
				teamId={teamIdA}
				teamType="H"
				teamName={teamNameA}
				isLive={isLive}
				score={scoreA}
				redCardCount={homeRedCardCount}
				isStrong={strongTeam === StrongTeam.Home}
			/>
			<TeamCard
				dotTemplate={() => dotTemplate('teamB')}
				teamId={teamIdB}
				teamType="A"
				teamName={teamNameB}
				isLive={isLive}
				score={scoreB}
				redCardCount={awayRedCardCount}
				isStrong={strongTeam === StrongTeam.Away}
			/>
		</div>
	);
});